<template>
    <div>
        <div class="input-group">
            <input v-if="!s3Path" type="file" class="form-control" id="input" 
                @change="handleFileUpload"
                :accept="acceptedFileTypesString"
                :class="{ 'is-invalid': pathError }" />
            <input v-else type="text" class="form-control" id="input" 
                :value="s3Path" 
                placeholder="Select a file path"
                :class="{ 'is-invalid': pathError }" 
                readonly />
            <button :class="['btn', $store.state.darkMode ? 'btn-secondary' : 'btn-outline-secondary']" type="button" @click="openFileBrowser">
                <span class="ms-1">Browse</span>
            </button>
            <button v-if="s3Path" class="btn btn-outline-danger" type="button" @click="clearPath"
                title="Clear path">
                <i class="fa fa-times"></i>
            </button>
        </div>
        <div v-if="pathError" class="invalid-feedback d-block">
            {{ pathError }}
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { MAX_PARSABLE_FILE_SIZE, isParsableFile } from '../utils/fileBrowser';

export default {
    name: "BrowseFile",
    props: {
        value: {
            type: Object,
            default: () => ({
                s3Path: '',
                presignedUrl: null,
                localFile: null,
                pathError: '',
                fileContent: null,
                credential: null,
                size: 0
            })
        },
        acceptedFileTypes: {
            type: Array,
            default: () => ['csv', 'json', 'avro','parquet'],
            validator: function (value) {
                return Array.isArray(value) && value.length > 0;
            }
        },
        generateURL:{
            type: Boolean,
            default: true
        },
        maxFileSize: {
            type: Number,
            default: 5 * 1024 * 1024 // 5MB in bytes
        }
    },
    data() {
        return {
            s3Path: this.value?.s3Path || '',
            pathError: this.value?.pathError || '',
            localFile: this.value?.localFile || null,
            presignedUrl: this.value?.presignedUrl || null,
            fileContent: this.value?.fileContent || null,
            // credential the remote file was browsed with - pail needs it to read the
            // object back for parsing
            credential: this.value?.credential || null,
            size: this.value?.size || 0
        }
    },
    computed: {
        acceptedFileTypesString() {
            return this.acceptedFileTypes.map(type => `.${type}`).join(',');
        },
        isFileSelected() {
            return this.s3Path 
                ? !!(this.presignedUrl && !this.pathError)
                : !!(this.localFile && this.fileContent && !this.pathError);
        }
    },
    watch: {
        s3Path() { this.emitValue(); },
        presignedUrl() { this.emitValue(); },
        localFile() { this.emitValue(); },
        fileContent() { this.emitValue(); },
        pathError() { this.emitValue(); },
        credential() { this.emitValue(); },
        size() { this.emitValue(); },
        value: {
            handler(newVal) {
                if (newVal && typeof newVal === 'object') {
                    if (this.s3Path !== newVal.s3Path) {
                        this.s3Path = newVal.s3Path || '';
                    }
                    if (this.pathError !== newVal.pathError) {
                        this.pathError = newVal.pathError || '';
                    }
                    if (this.localFile !== newVal.localFile) {
                        this.localFile = newVal.localFile || null;
                    }
                    if (this.presignedUrl !== newVal.presignedUrl) {
                        this.presignedUrl = newVal.presignedUrl || null;
                    }
                    if (this.fileContent !== newVal.fileContent) {
                        this.fileContent = newVal.fileContent || null;
                    }
                    if (this.credential !== newVal.credential) {
                        this.credential = newVal.credential || null;
                    }
                    if (this.size !== newVal.size) {
                        this.size = newVal.size || 0;
                    }
                }
            },
            deep: true,
            immediate: true
        }
    },
    methods: {
        ...mapActions({
            showModal: "showModal"
        }),
        emitValue() {
            const payload = {
                s3Path: this.s3Path,
                presignedUrl: this.presignedUrl,
                credential: this.credential,
                size: this.size,
                localFile: this.localFile,
                fileContent: this.fileContent,
                pathError: this.pathError,
                isFileSelected: this.isFileSelected
            };
            this.$emit('input', payload);
        },
        openFileBrowser() {
            this.showModal({
                component: 'FileSelectionModal',
                data: {
                    title: 'Select a File',
                    onFileSelected: this.handleS3FileSelection,
                    generateURL:this.generateURL
                }
            });
        },
        validateFileExtension(fileName) {
            const lowerFileName = fileName.toLowerCase();
            const hasValidExtension = this.acceptedFileTypes.some(type => 
                lowerFileName.endsWith(`.${type.toLowerCase()}`)
            );

            if (!hasValidExtension) {
                const extensions = this.acceptedFileTypes.map(type => `.${type.toLowerCase()}`).join(', ');
                this.pathError = `Selected file must be one of the following types: ${extensions}`;
                return false;
            }

            this.pathError = '';
            return true;
        },
        clearPath() {
            this.s3Path = '';
            this.pathError = '';
            this.presignedUrl = null;
            this.credential = null;
            this.size = 0;
        },
        // avro/parquet are read end to end by pail's parser before they can be used,
        // so the same size ceiling applies to browsed files as to uploaded ones
        validateParsableSize(fileName, size) {
            if (!isParsableFile(fileName) || !size || size <= MAX_PARSABLE_FILE_SIZE) {
                return true;
            }
            const maxSizeMB = Math.round(MAX_PARSABLE_FILE_SIZE / (1024 * 1024));
            this.pathError = `${fileName.split('/').pop()} is larger than ${maxSizeMB}MB and cannot be parsed`;
            return false;
        },
        handleS3FileSelection(fileData) {
            this.localFile = null;
            this.fileContent = null;
            this.clearFileInput();
            
            this.s3Path = fileData.s3Path;
            this.presignedUrl = fileData.presignedUrl;
            this.credential = fileData.credential;
            this.size = fileData.size || 0;

            if (!fileData.s3Path) {
                this.pathError = 'Please select a file path';
            } else if (this.validateFileExtension(fileData.s3Path)) {
                this.validateParsableSize(fileData.s3Path, this.size);
            }
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            this.localFile = null;
            this.fileContent = null;
            this.pathError = '';
            if (file) {
                this.localFile = file;
                if (this.validateLocalFile(file)) {
                    this.s3Path = '';
                    this.presignedUrl = null;
                    this.readFileContent(file);
                } 
            }
        },
        readFileContent(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                this.fileContent = e.target.result;
            };
            reader.onerror = () => {
                this.pathError = 'Error reading file';
                this.fileContent = null;
            };
            reader.readAsText(file);
        },
        validateLocalFile(file) {
            if (!file) {
                this.pathError = 'Please select a file';
                return false;
            }

            if (!this.validateFileExtension(file.name)) {
                return false;
            }

            if (file.size > this.maxFileSize) {
                const maxSizeMB = Math.round(this.maxFileSize / (1024 * 1024));
                this.pathError = `File size must be less than ${maxSizeMB}MB`;
                return false;
            }

            this.pathError = '';
            return true;
        },
        clearFileInput() {
            const fileInput = document.getElementById("input");
            if (fileInput && fileInput.type === 'file') {
                fileInput.value = '';
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.emitValue();
        });
    }
};
</script>

<style scoped>
.invalid-feedback.d-block {
    display: block !important;
    color: #dc3545;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}
</style>

<style lang="scss">
@use '../theme/scss/_user-variables.scss' as *;

body.dark-mode {
    /* Target the file input container */
    .form-control[type="file"] {
        background-color: #111317 !important; // Deep black/dark background
        color: #a0a4b8 !important;            // Muted text color for "No file chosen"
        border-color: #2e3238 !important;

        /* Target the "Choose File" button specifically */
        &::file-selector-button {
            background-color: #22252b !important; // Slightly lighter than the input bg
            color: #e1e3e8 !important;            // Bright text for the button
        }
    }
}
</style>